/**
 * T-090 — the maturity arrow grid (kit, data-kit="maturity-grid"). Levels 0–5 as columns, a
 * filled marker at the current level, a hollow marker at the target, and an arrow between them
 * whose length is the gap. Colour never carries meaning alone: the two markers differ in shape,
 * the arrow has a head, and the whole grid carries a text alternative.
 *
 * Honest with the data: a literal 0 is a level (a marker in the 0 column); an unassessed level
 * (null/undefined) draws no marker and the text says "not assessed".
 */
import * as React from 'react';

export const MATURITY_LEVELS = [0, 1, 2, 3, 4, 5] as const;
const LAST = MATURITY_LEVELS.length - 1;

export interface MaturityGridProps {
  /** Current level 0–5; null or undefined = not assessed (no marker). */
  current: number | null | undefined;
  /** Target level 0–5; null or undefined = no target set (no marker). */
  target: number | null | undefined;
  /** Prints the 0–5 scale under the columns (the record page and the request form). */
  showScale?: boolean;
  /** What the levels are about, for the text alternative ("Access control"). */
  label?: string;
}

function level(v: number | null | undefined): number | null {
  if (v === null || v === undefined || !Number.isFinite(v)) return null;
  return Math.min(Math.max(Math.round(v), 0), LAST);
}

/** The words a reader hears (and sees as a tooltip): current, target and the gap between. */
export function maturityText(current: number | null | undefined, target: number | null | undefined): string {
  const c = level(current);
  const t = level(target);
  const now = c === null ? 'Current level not assessed' : `Current level ${c}`;
  const goal = t === null ? 'no target set' : `target level ${t}`;
  if (c === null || t === null) return `${now}, ${goal}`;
  if (c === t) return `${now}, ${goal}: on target`;
  const gap = t - c;
  return `${now}, ${goal}: ${gap > 0 ? `${gap} ${gap === 1 ? 'level' : 'levels'} to go` : `${-gap} ${gap === -1 ? 'level' : 'levels'} above target`}`;
}

/** Centre of a level's column as a percentage of the grid width. */
const centre = (l: number) => ((l + 0.5) / MATURITY_LEVELS.length) * 100;

export function MaturityGrid({ current, target, showScale, label }: MaturityGridProps) {
  const c = level(current);
  const t = level(target);
  const text = maturityText(current, target);
  const arrow = c !== null && t !== null && c !== t;
  const from = arrow ? centre(c) : 0;
  const to = arrow ? centre(t) : 0;
  return (
    <span
      className="k-mgrid"
      data-kit="maturity-grid"
      data-current={c ?? 'none'}
      data-target={t ?? 'none'}
      role="img"
      aria-label={label ? `${label}: ${text}` : text}
      title={text}
    >
      <span className="k-mgrid-cols" aria-hidden>
        {MATURITY_LEVELS.map((l) => (
          <span key={l} className="k-mgrid-col" data-level={l}>
            {t === l ? <span className="k-mgrid-target" data-marker="target" /> : null}
            {c === l ? <span className="k-mgrid-current" data-marker="current" /> : null}
          </span>
        ))}
        {arrow ? (
          <span
            className="k-mgrid-arrow"
            data-direction={t! > c! ? 'up' : 'down'}
            data-gap={t! - c!}
            style={{ left: `${Math.min(from, to)}%`, right: `${100 - Math.max(from, to)}%` }}
          />
        ) : null}
      </span>
      {showScale ? (
        <span className="k-mgrid-scale" aria-hidden>
          {MATURITY_LEVELS.map((l) => (
            <span key={l}>{l}</span>
          ))}
        </span>
      ) : null}
    </span>
  );
}

/** The key under a grid where one is first shown (the list's header help, the form). */
export function MaturityGridKey() {
  return (
    <span className="k-mgrid-key k-meta">
      <span className="k-mgrid-current" aria-hidden /> where it is today
      <span className="k-mgrid-target" aria-hidden /> where it is going
      <span className="k-mgrid-key-arrow" aria-hidden /> the gap
    </span>
  );
}

export default React.memo(MaturityGrid);
